import { CHESS_MODEL_STRATEGY } from "./chessProbability";
import { BETTY_MODEL_STRATEGY, calculateBettyChessProbabilities } from "./chessBettyStrength";
import { buildGameProbabilities, fairOdd } from "./chessTournamentSimulation";

const OUTCOMES = [
  { key: "teamAWin", score: 1 },
  { key: "draw", score: 0.5 },
  { key: "teamBWin", score: 0 },
];

export function buildBoardProbabilities({ teamAPlayer, teamBPlayer, teamAColor, effectiveMinutes, parameters, modelStrategy = CHESS_MODEL_STRATEGY, strengthReference }) {
  const teamAWhite = teamAColor === "white";
  const white = teamAWhite ? teamAPlayer : teamBPlayer;
  const black = teamAWhite ? teamBPlayer : teamAPlayer;
  if (![CHESS_MODEL_STRATEGY, BETTY_MODEL_STRATEGY].includes(modelStrategy)) throw new Error("Unknown chess model.");
  const probabilities = modelStrategy === BETTY_MODEL_STRATEGY ? calculateBettyChessProbabilities({
    player1Elo: white.elo, player2Elo: black.elo, player1Color: "white",
    player1DrawFactor: white.drawFactor, player2DrawFactor: black.drawFactor,
    effectiveMinutes, parameters, strengthReference,
  }) : buildGameProbabilities({
    game: { whitePlayerId: white.id, blackPlayerId: black.id },
    playersById: new Map([[white.id, white], [black.id, black]]),
    effectiveMinutes,
    parameters,
  });
  const result = teamAWhite ? {
    teamAWin: probabilities.player1Win,
    draw: probabilities.draw,
    teamBWin: probabilities.player2Win,
  } : {
    teamAWin: probabilities.player2Win,
    draw: probabilities.draw,
    teamBWin: probabilities.player1Win,
  };
  return {
    ...result,
    modelStrategy,
    strengthReference: probabilities.strengthReference,
    effectiveEloDifference: probabilities.effectiveEloDifference,
    fairOdds: {
      teamAWin: fairOdd(result.teamAWin),
      draw: fairOdd(result.draw),
      teamBWin: fairOdd(result.teamBWin),
    },
  };
}

export function calculateTeamMatchProbabilities(boardProbabilities, knownScores = []) {
  if (!Array.isArray(boardProbabilities) || boardProbabilities.length !== 4) {
    throw new Error("A team match requires exactly four boards.");
  }
  const strategies = new Set(boardProbabilities.map(board => board.modelStrategy || CHESS_MODEL_STRATEGY));
  if (strategies.size !== 1) throw new Error("A team forecast cannot mix chess models.");
  if (strategies.has(BETTY_MODEL_STRATEGY)) {
    const first = boardProbabilities[0].strengthReference;
    if (!first || boardProbabilities.some(board => board.strengthReference?.section !== first.section
        || board.strengthReference?.referenceElo !== first.referenceElo)) {
      throw new Error("A team forecast cannot mix section references.");
    }
  }
  const result = { teamAWin: 0, draw: 0, teamBWin: 0, combinations: 0 };
  const visit = (board, score, probability) => {
    if (board === boardProbabilities.length) {
      result.combinations += 1;
      if (score > 0) result.teamAWin += probability;
      else if (score === 0) result.draw += probability;
      else result.teamBWin += probability;
      return;
    }
    if (knownScores[board] !== null && knownScores[board] !== undefined) {
      const known = knownScores[board];
      const pair = Array.isArray(known) ? known : [known, 1 - known];
      if (pair.length !== 2 || !pair.every(value => [0, 0.5, 1].includes(value))
          || ![0, 1].includes(pair[0] + pair[1])) throw new Error("Invalid known board score.");
      visit(board + 1, score + pair[0] - pair[1], probability);
      return;
    }
    OUTCOMES.forEach((outcome) => visit(
      board + 1,
      score + 2 * outcome.score - 1,
      probability * Number(boardProbabilities[board][outcome.key]),
    ));
  };
  visit(0, 0, 1);
  return {
    ...result,
    fairOdds: {
      teamAWin: fairOdd(result.teamAWin),
      draw: fairOdd(result.draw),
      teamBWin: fairOdd(result.teamBWin),
    },
  };
}
